import { useState } from 'react';

import { ArtificialIntelligenceApi } from 'Api/ArtificialIntelligence/ArtificialIntelligenceApi';
import { ControlsApi } from 'Api/Controls/ControlsApi';
import { FormFieldText } from 'Components/FormField/FormFieldText/FormFieldText';
import { FormFieldTextAreaAi } from 'Components/FormField/FormFieldTextAreaAi/FormFieldTextAreaAi';
import { useAiContentAdvisor } from 'Hooks/AiContentAdvisor';
import { AiContentAdvisorFeature } from 'Models/ArtificialIntelligence';
import { SaveControlGroupRequest } from 'Models/OperationalControls';

import styles from './SaveControlGroup.module.css';

type CreateControlGroupStatus = 'idle' | 'saving' | 'saved' | 'error';

interface SaveControlGroupFormFieldsValues {
    customControlGroupName?: string;
    customControlGroupDescription?: string;
}

export const useSaveControlGroup = (controlFramework: string, controlsApi: ControlsApi, defaultSaveControlGroupFormFieldsValues: SaveControlGroupFormFieldsValues, existingControlGroupId?: string) => {
    const [saveControlGroupStatus, setSaveControlGroupStatus] = useState<CreateControlGroupStatus>('idle');
    const [error, setError] = useState<Error>();
    const [saveControlGroupFormFieldsValues, setSaveControlGroupFormFieldsValues] = useState<SaveControlGroupFormFieldsValues>({ ...defaultSaveControlGroupFormFieldsValues });

    const saveControlGroup = async () => {
        setSaveControlGroupStatus('saving');
        try {
            if (saveControlGroupFormFieldsValues.customControlGroupName && saveControlGroupFormFieldsValues.customControlGroupDescription) {
                const saveControlGroup: SaveControlGroupRequest = {
                    control_group_name: saveControlGroupFormFieldsValues.customControlGroupName,
                    control_group_description: saveControlGroupFormFieldsValues.customControlGroupDescription,
                };
                await controlsApi.saveCustomControlGroup(controlFramework, saveControlGroup, existingControlGroupId);
                setSaveControlGroupStatus('saved');
            } else {
                setSaveControlGroupStatus('error');
                setError(new Error('Error: Not all required fields have values.'));
            }
        } catch (err) {
            handleRequestError(err);
        }
    };

    const handleRequestError = (error: Error) => {
        setSaveControlGroupStatus('error');
        setError(error);
    };

    const handleChange = (event: React.FormEvent<HTMLInputElement>): void => {
        setSaveControlGroupFormFieldsValues({ ...saveControlGroupFormFieldsValues, [event.currentTarget.name]: event.currentTarget.value });
    };

    return {
        saveControlGroupStatus: saveControlGroupStatus,
        saveControlGroupFormFieldsValues: saveControlGroupFormFieldsValues,
        error: error,
        saveControlGroup: saveControlGroup,
        handleChange: handleChange,
    };
};

export interface SaveControlGroupFromFieldsProps {
    artificialIntelligenceApi: ArtificialIntelligenceApi;
    saveControlGroupFormFieldsValues: SaveControlGroupFormFieldsValues;
    handleChange: (event: React.FormEvent<HTMLInputElement>) => void;
    disabled?: boolean;
}

export const SaveControlGroupFormFields = ({ disabled = false, ...props }: SaveControlGroupFromFieldsProps) => {
    const controlGroupDescriptionAi = useAiContentAdvisor(props.artificialIntelligenceApi, props.saveControlGroupFormFieldsValues.customControlGroupDescription ?? '', AiContentAdvisorFeature.GOVERNANCE_AND_COMPLIANCE_CONTROL_GROUP_DESCRIPTION, () => undefined);
    return (
        <>
            <div className={styles.formFieldContainer}>
                <FormFieldText disabled={disabled} value={props.saveControlGroupFormFieldsValues.customControlGroupName} formFieldId="customControlGroupName" formFieldLabel="Name" handleChange={props.handleChange} tooltip="The name used to identify this control group." required />
            </div>
            <div className={styles.formFieldContainer}>
                <FormFieldTextAreaAi disabled={disabled} formFieldId="customControlGroupDescription" value={props.saveControlGroupFormFieldsValues.customControlGroupDescription} formFieldLabel="Description" handleChange={props.handleChange} tooltip="A description of what is assessed by the controls in this control group." required aiContentAdvisorValues={controlGroupDescriptionAi} />
            </div>
        </>
    );
};
